'use strict';

import ActionTypes from '../constants/ActionTypes';
import AppDispatcher from '../AppDispatcher';
import ActionUtils from '../utils/ActionUtils';
import StampAPI from '../api/StampAPI';
import DraftAPI from '../api/DraftAPI';
import StampDraftStore from '../stores/StampDraftStore';
import AlertActionCreators from '../actions/AlertActionCreators';
import StampActionCreators from '../actions/StampActionCreators';
import AlertTypes from '../constants/AlertTypes';
import GrabbagImageStore from '../stores/GrabbagImageStore';
import DraftFeedbackStore from '../stores/DraftFeedbackStore';
import getRefinedStamp from '../utils/getRefinedStamp';
import { goBack, replaceWith, transitionTo } from '../router';
import getStampSegment from '../utils/getStampSegment';
import parseStampSegment from '../utils/parseStampSegment';

var DraftActionCreators = {
  deleteSection(sectionId) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.CANVAS_DELETE_SECTION,
      sectionId: sectionId
    });
  },

  addGalleryImage(sectionId, image) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.GALLERY_ADD_IMAGE,
      sectionId: sectionId,
      image: image
    });
  },

  updateGalleryImage(sectionId, imageId, data) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.GALLERY_UPDATE_IMAGE,
      sectionId: sectionId,
      imageId: imageId,
      data: data
    });
  },

  moveGalleryImage(sectionId, imageId, afterImageId) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.GALLERY_MOVE_IMAGE,
      sectionId: sectionId,
      imageId: imageId,
      afterImageId: afterImageId
    });
  },

  deleteGalleryImage(sectionId, imageId) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.GALLERY_DELETE_IMAGE,
      sectionId: sectionId,
      imageId: imageId
    });
  },

  addSection(sectionType, afterSectionId, initialOptions) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.CANVAS_ADD_SECTION,
      sectionType: sectionType,
      afterSectionId: afterSectionId,
      initialOptions: initialOptions
    });
  },

  moveSection(sectionId, afterSectionId) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.CANVAS_MOVE_SECTION,
      sectionId: sectionId,
      afterSectionId: afterSectionId
    });
  },

  updateSection(sectionId, data) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.CANVAS_UPDATE_SECTION,
      sectionId: sectionId,
      data: data
    });
  },

  loadStampInEditor(stampId) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.LOAD_STAMP_IN_EDITOR,
      stampId: stampId
    });

    DraftAPI.loadStampInEditor(stampId);
  },

  loadDraftInEditor(draftUuid) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.LOAD_DRAFT_IN_EDITOR,
      draftUuid: draftUuid
    });

    DraftAPI.loadDraftInEditor(draftUuid);
  },

  loadEmptyDraft() {
    AppDispatcher.handleViewAction({
      type: ActionTypes.LOAD_EMPTY_DRAFT
    });
  },

  resetLastAddedItem() {
    AppDispatcher.handleViewAction({
      type: ActionTypes.DRAFT_RESET_LAST_ADDED_ITEM
    });
  },

  cancelEditingStamp(disregardChanges) {
    if (StampDraftStore.hasUnsavedChanges() && !disregardChanges) {
      AlertActionCreators.showAlert(AlertTypes.EDITOR_DISREGARD_CHANGES);
      return;
    }

    if (!goBack()) {
      const draft = StampDraftStore.getDraftStamp();
      transitionTo('feedStamp', {
        stampSegment: getStampSegment(draft)
      });
    }
  },

  switchToCompose() {
    const draft = StampDraftStore.getDraftStamp();
    const stampSegment = getStampSegment(draft);
    const { isStamp } = parseStampSegment(stampSegment);

    if (isStamp) {
      replaceWith('stampEditCompose', { stampSegment });
    } else {
      replaceWith('draftCompose', { stampSegment });
    }
  },

  switchToPreview() {
    const draft = StampDraftStore.getDraftStamp();
    const stampSegment = getStampSegment(draft);
    const { isStamp } = parseStampSegment(stampSegment);

    if (isStamp) {
      replaceWith('stampEditPreview', { stampSegment });
    } else {
      replaceWith('draftPreview', { stampSegment });
    }
  },

  saveDraft() {
    const stamp = StampDraftStore.getDraftStamp();
    stamp.grabbag = GrabbagImageStore.getUploadedImageItems();

    AppDispatcher.handleViewAction({
      type: ActionTypes.SAVE_DRAFT,
      entity: stamp
    });

    DraftAPI.saveDraft(stamp);
  },

  postOrUpdateStamp() {
    if (!StampDraftStore.canPublish()) {
      return;
    }

    var messageId = StampDraftStore.getMissingContentMessage();
    if (messageId) {
      AlertActionCreators.showAlert(messageId, { hideOnRouteChange: false });
      DraftActionCreators.switchToCompose();
      return;
    }

    var stamp = getRefinedStamp(StampDraftStore.getDraftStamp());
    if (stamp.id) {
      StampActionCreators.updateStamp(stamp);
    } else {
      ActionUtils.postEntity({
        entity: stamp,
        action: ActionTypes.POST_STAMP,
        callAPI: StampAPI.postStamp
      });
    }
  },

  deleteDraft(draftUuid) {
    ActionUtils.deleteEntity({
      idAttribute: 'draftUuid',
      draftUuid: draftUuid,
      store: StampDraftStore,
      action: ActionTypes.DELETE_STAMP,
      callAPI: DraftAPI.deleteDraft
    });
  },

  sendFeedback(draftUuid, message) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.SEND_DRAFT_FEEDBACK,
      draftUuid: draftUuid
    });

    DraftAPI.sendFeedback(draftUuid, message);
  },

  requestDraftPage(isInitialRequest) {
    if (isInitialRequest) {
      AppDispatcher.handleViewAction({
        type: ActionTypes.INVALIDATE_DRAFTS
      });
    }

    ActionUtils.requestPage({
      isInitialRequest: isInitialRequest,
      store: StampDraftStore,
      action: ActionTypes.REQUEST_DRAFTS,
      callAPI: DraftAPI.requestDraftsPage
    });
  },

  requestDraftFeedbackPage(draftUuid, isInitialRequest) {
    if (isInitialRequest) {
      AppDispatcher.handleViewAction({
        type: ActionTypes.INVALIDATE_DRAFT_FEEDBACKS,
        draftUuid: draftUuid
      });
    }

    ActionUtils.requestIndexedPage({
      draftUuid: draftUuid,
      idAttribute: 'draftUuid',
      isInitialRequest: isInitialRequest,
      store: DraftFeedbackStore,
      action: ActionTypes.REQUEST_DRAFT_FEEDBACKS,
      callAPI: DraftAPI.requestDraftFeedbacksPage
    });
  },

  setStatus(status) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.DRAFT_SET_STATUS,
      status: status
    });
  }
};

module.exports = DraftActionCreators;


/** WEBPACK FOOTER **
 ** ./actions/DraftActionCreators.js
 **/